// src/components/Strategies.js
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Grid, Card, CardContent, CardHeader, Button, Typography, Box } from "@mui/material";
import { Allstrategies } from "../../Redux/Actions/strategyService";
import Loader from "../../Loader";

import { useNavigate } from "react-router-dom";
import "../../Styles/strategy.css";
import DistributionChart from "../Charts/DistributionChart";
import { GetUserInfo } from "../../Redux/Actions/userStatusService";

function Strategies({ custodian }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { strategies, loading } = useSelector((state) => state.strategy);
  const { userInformation, users } = useSelector((state) => state.userStatus);
  const userInfoString = localStorage.getItem("userInfo");
  const userInfo = userInfoString && userInfoString !== "undefined" ? JSON.parse(userInfoString) : null;
  const portal = userInfo?.portal || 'MANAGER';
  const id = userInformation?.entityInfo?.id;
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const strategiesPerPage = 8;


  useEffect(() => {
    const fetchData = async () => {
      try {
        let userInfoResponse = null;

        if (portal.toLowerCase() === "manager") {
          userInfoResponse = await dispatch(
            GetUserInfo({ role: portal })
          ).unwrap();
        } else if (users && users.length > 0) {
          const parent = users[0]?.parentUserRole?.id;

          if (parent) {
            userInfoResponse = await dispatch(
              GetUserInfo({ role: portal, id: parent })
            ).unwrap();
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, [dispatch, portal, users]);
  
  // Reset to page 1 when custodian changes
  useEffect(() => {
    setCurrentPage(1);
  }, [custodian]);

  useEffect(() => {
    const skip = (currentPage - 1) * strategiesPerPage;
    const take = strategiesPerPage; // Always fetch exactly 8 strategies

    const params = {
      skip: skip,
      take: take,
    };

    // Only add custodian parameter if not "All"
    if (custodian !== "All") {
      params.custodian = custodian;
    }

    if (portal.toLowerCase() !== "manager" && users && users.length > 0) {
      const parent = users[0]?.parentUserRole?.id;
      if (parent) {
        params.parent = parent;
      }
    }

    dispatch(Allstrategies(params)).then((response) => {
  
      
      // Try different possible response structures
      let fetchedStrategies = null;
      
      if (response?.payload?.data?.strategies) {
        fetchedStrategies = response.payload.data.strategies;
      } else if (response?.payload?.data && Array.isArray(response.payload.data)) {
        fetchedStrategies = response.payload.data;
      } else if (response?.payload?.strategies) {
        fetchedStrategies = response.payload.strategies;
      } else if (response?.data?.strategies) {
        fetchedStrategies = response.data.strategies;
      } else if (response?.data && Array.isArray(response.data)) {
        fetchedStrategies = response.data;
      }
      
      if (fetchedStrategies && Array.isArray(fetchedStrategies)) {
        // If we get exactly 8 strategies, assume there might be more (enable next)
        // If we get less than 8, there's no next page
        const hasNext = fetchedStrategies.length === strategiesPerPage;
        setHasNextPage(hasNext);
      } else {
        setHasNextPage(false);
      }
    }).catch((error) => {
      setHasNextPage(false);
    });
  }, [dispatch, currentPage, custodian, id, portal, users]);

  const trimText = (text, length) =>
    text.length > length ? `${text.substring(0, length)}...` : text;

  // Display all fetched strategies (exactly 8 or less)
  const displayStrategies = strategies || [];
  

  return (
    <>
      <div style={{ height: "calc(100vh - 170px)", overflowY: "auto", padding: "20px" }}>
        <Grid container spacing={3}>
          {loading ? (
            <Loader />
          ) : displayStrategies.length > 0 ? (
            displayStrategies?.map((values, index) => (
              <Grid item key={values.id} xs={12} sm={6} md={4} lg={3}>
                <Card sx={{
                  borderRadius: '10px',
                  boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.1)',
                  height: '100%'
                }}>
                  <CardContent>
                    <Typography 
                      variant="h6" 
                      sx={{
                        fontSize: '16px',
                        fontWeight: 500,
                        mb: 1
                      }}
                    >
                      {trimText(values?.name, 25)}
                    </Typography>
                    
                    <Box sx={{ 
                      my: 2, 
                      display: 'flex', 
                      justifyContent: 'center', 
                      alignItems: 'center' 
                    }}>
                      <DistributionChart proportion={values.breakups} />
                    </Box>

                    <Box sx={{ 
                      display: 'flex', 
                      gap: 1, 
                      mb: 1 
                    }}>
                      <Button
                        variant="outlined"
                        size="small"
                        sx={{
                          flex: 1,
                          textTransform: 'none',
                          borderColor: '#1976d2',
                          color: '#1976d2'
                        }}
                        onClick={() => navigate(`/performance/${values?.id}`)}
                      >
                        Performance
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        sx={{
                          flex: 0.5,
                          textTransform: 'none',
                          borderColor: '#1976d2',
                          color: '#1976d2'
                        }}
                        onClick={() => navigate(`/editstrategy/${values.id}`)}
                      >
                        Edit
                      </Button>
                    </Box>

                    <Button
                      variant="contained"
                      fullWidth
                      sx={{
                        textTransform: 'none',
                        backgroundColor: '#1976d2'
                      }}
                      onClick={() => navigate(`/rebalancing/${values.id}`)}
                    >
                      Rebalance
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))
          ) : (
            <Grid item xs={12}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '400px',
                textAlign: 'center',
                py: 4
              }}>
                {/* Icon/Illustration */}
                <Box sx={{
                  width: 120,
                  height: 120,
                  borderRadius: '50%',
                  bgcolor: '#F5F7FA',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 3,
                  border: '2px solid #E1E8EF'
                }}>
                  <Box sx={{
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    bgcolor: '#E1E8EF',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Typography sx={{ 
                      fontSize: '24px',
                      color: '#9CA3AF'
                    }}>
                      📊
                    </Typography>
                  </Box>
                </Box>

                {/* Main Message */}
                <Typography variant="h5" sx={{ 
                  fontWeight: 600,
                  color: '#374151',
                  mb: 1
                }}>
                  No Strategies Available
                </Typography>

                {/* Subtitle */}
                <Typography variant="body1" sx={{ 
                  color: '#6B7280',
                  mb: 3,
                  maxWidth: '400px',
                  lineHeight: 1.5
                }}>
                  {custodian === "All" 
                    ? "There are currently no strategies available across all custodians. Create your first strategy to get started." 
                    : `No strategies found for ${custodian}. Try selecting a different custodian or create a new strategy.`
                  }
                </Typography>

                {/* Action Button */}
                <Button
                  variant="contained"
                  sx={{
                    bgcolor: '#1976d2',
                    color: 'white',
                    px: 4,
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 500,
                    '&:hover': {
                      bgcolor: '#1565c0'
                    }
                  }}
                  onClick={() => navigate('/addstrategy')}
                >
                  Create New Strategy
                </Button>

                {/* Additional Help Text */}
                <Typography variant="caption" sx={{ 
                  color: '#9CA3AF',
                  mt: 2,
                  fontSize: '12px'
                }}>
                  Need help? Contact support or check our documentation
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>

        {displayStrategies.length > 0 && (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            mt: 3,
            gap: 2
          }}>
            <Button
              variant="contained"
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Previous
            </Button>
            <Typography 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                px: 2,
                fontWeight: 500 
              }}
            >
              Page {currentPage}
            </Typography>
            <Button
              variant="contained"
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={!hasNextPage}
            >
              Next
            </Button>
          </Box>
        )}
      </div>
    </>
  );
}

export default Strategies;
